import {
  MarketingTriggerKey,
  OrderTrackingTriggerKey,
} from "@redotech/redo-flows-common/triggers";
import {
  ShopperAgentTriggerSkipConditionsSchema,
  TriggerProcessingPolicyConfigSchema,
} from "@redotech/redo-shopper-agent-common/trigger-config";
import { getTriggerById } from "@redotech/redo-shopper-agent-common/triggers";
import { ShopperAgentAutomationConfigSchema } from "@redotech/redo-shopper-agent-settings-common/automation-config";
import { ShopperAgentCooldownConfigSchema } from "@redotech/redo-shopper-agent-settings-common/cooldown-config";
import { ShopperAgentDiscountConfigSchema } from "@redotech/redo-shopper-agent-settings-common/discount-config";
import { ShopperAgentIMessageConfigSchema } from "@redotech/redo-shopper-agent-settings-common/imessage-config";
import { ShopperAgentReplyConfigSchema } from "@redotech/redo-shopper-agent-settings-common/reply-config";
import { SkillConfigSchema } from "@redotech/redo-shopper-agent-settings-common/skill-config";
import { ShopperAgentSupervisorConfigSchema } from "@redotech/redo-shopper-agent-settings-common/supervisor-config";
import * as z from "zod";

/**
 * Input schema for updating a trigger.
 *
 * - Pass `config: null` to remove the entire trigger override.
 * - Within config, pass a field as `null` to remove that specific override
 *   (e.g. `discountConfig: null` removes the discount override so it inherits
 *   from base). Pass `undefined`/omit to leave unchanged.
 */
const TriggerUpdateConfigSchema = z.object({
  enabled: z.boolean().optional(),
  iMessageConfig: ShopperAgentIMessageConfigSchema.nullable().optional(),
  replyConfig: ShopperAgentReplyConfigSchema.nullable().optional(),
  discountConfig: ShopperAgentDiscountConfigSchema.nullable().optional(),
  cooldownConfig: ShopperAgentCooldownConfigSchema.nullable().optional(),
  automations: z
    .array(ShopperAgentAutomationConfigSchema)
    .nullable()
    .optional(),
  skills: z.record(z.string(), SkillConfigSchema).nullable().optional(),
  guidance: z.string().nullable().optional(),
  supervisorConfig: ShopperAgentSupervisorConfigSchema.nullable().optional(),
  skipConditions: ShopperAgentTriggerSkipConditionsSchema.nullable().optional(),
  processingPolicy: TriggerProcessingPolicyConfigSchema.nullable().optional(),
});

const TriggerIdSchema = z.enum([
  ...Object.values(MarketingTriggerKey),
  ...Object.values(OrderTrackingTriggerKey),
]);

export const updateShopperAgentTrigger = {
  name: "updateShopperAgentTrigger" as const,
  input: z
    .object({
      triggerId: TriggerIdSchema,
      config: TriggerUpdateConfigSchema.nullable(),
      stageDraft: z.boolean().optional(),
    })
    .refine(
      ({ triggerId, config }) => {
        if (!config) {
          return true;
        }
        const automations = config.automations;
        if (!automations || automations.length === 0) {
          return true;
        }
        const def = getTriggerById(triggerId);
        // `supportsAutomations` is `false` only for triggers whose advanced
        // flows must always fire outside the shopper agent (e.g. SMS_SIGNUP).
        return def?.supportsAutomations !== false;
      },
      {
        error:
          "This trigger does not support attaching automations — its flows are dispatched outside Commerce Agent.",
        path: ["config", "automations"],
      },
    )
    .refine(
      ({ triggerId, config }) => {
        if (!config?.processingPolicy?.buffer) {
          return true;
        }
        if (config.processingPolicy.buffer.bypass === undefined) {
          return true;
        }
        const def = getTriggerById(triggerId);
        return def?.processingPolicy?.buffer?.supportsOverride === true;
      },
      {
        error: "This trigger does not support buffer bypass overrides.",
        path: ["config", "processingPolicy", "buffer", "bypass"],
      },
    )
    .refine(
      ({ triggerId, config }) => {
        if (!config?.processingPolicy?.sentinel) {
          return true;
        }
        if (config.processingPolicy.sentinel.bypass === undefined) {
          return true;
        }
        const def = getTriggerById(triggerId);
        return def?.processingPolicy?.sentinel?.supportsOverride === true;
      },
      {
        error: "This trigger does not support sentinel bypass overrides.",
        path: ["config", "processingPolicy", "sentinel", "bypass"],
      },
    )
    .refine(
      ({ triggerId, config }) => {
        if (!config) {
          return true;
        }
        const skipConditions = config.skipConditions;
        if (!skipConditions || skipConditions.conditions.length === 0) {
          return true;
        }
        const def = getTriggerById(triggerId);
        // `supportsSkipConditions` is `false` for triggers where the
        // coordinator handoff happens without a SchemaInstance in scope
        // (today: SMS_SIGNUP). Reject saves so merchants can't configure
        // a state that would never be evaluated.
        return def?.supportsSkipConditions !== false;
      },
      {
        error:
          "This trigger does not support skip conditions — its routing happens outside the path where conditions are evaluated.",
        path: ["config", "skipConditions"],
      },
    ),
  output: z.object({ success: z.boolean() }),
};
